import { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { useToast } from './Toast';
import { TablaIvaPorAlicuota } from './TablaIvaVenta';
import { useApi } from '../hooks/useApi';
import { api, extractErrorMessage } from '../api/client';
import { ALICUOTAS_IVA, centavos, LETRAS_COMPROBANTE, numeroComprobanteVisible } from '../lib/comprobanteCompra';
import { etiquetaAlicuota, resumenIvaVenta } from '../lib/ivaVenta';
import { pideNumeroComprobante } from '../lib/comprobanteVenta';
import { useHayCambios } from '../hooks/useHayCambios';
import { useCierreConfirmado } from '../hooks/useCierreConfirmado';
import { useCamposObligatorios, faltaSi } from '../hooks/useCamposObligatorios';
import { Obligatorio, LeyendaObligatorio } from './Obligatorio';

/**
 * Alta de notas de crédito y débito de venta sobre una factura (el ERP registra el comprobante emitido fuera, igual que
 * la factura). La nota de crédito acredita renglones de la factura; la nota de débito lleva ítems libres.
 */

export interface FacturaOrigenNota { id: number; numeroFactura?: string | null; letra?: string | null }

type TipoNota = 'nota_credito' | 'nota_debito';

const today = () => new Date().toISOString().slice(0, 10);
const pesos = (n: number | string) => `$ ${Number(n).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cantidad4 = (n: number) => Math.round(n * 10000) / 10000;

interface Identidad { letra: string; puntoVenta: string; numeroComprobante: string; numeroFactura: string; cae: string; caeVencimiento: string; fechaEmision: string; notas: string }
const identidadVacia = (letra?: string | null): Identidad => ({
  letra: letra ?? '', puntoVenta: '', numeroComprobante: '', numeroFactura: '', cae: '', caeVencimiento: '', fechaEmision: today(), notas: '',
});

/** Número armado y error de la identidad del comprobante (mismas reglas que el backend). */
function validarIdentidad(f: Identidad, tipo: TipoNota) {
  const pv = Number(f.puntoVenta);
  const nro = Number(f.numeroComprobante);
  const pvValido = f.puntoVenta !== '' && Number.isInteger(pv) && pv >= 1 && pv <= 99999;
  const nroValido = f.numeroComprobante !== '' && Number.isInteger(nro) && nro >= 1 && nro <= 99999999;
  const numeroPrevio = pvValido && nroValido ? numeroComprobanteVisible(f.letra || null, pv, nro, tipo) : null;
  const error = (f.puntoVenta !== '' && !pvValido) ? 'El punto de venta va de 1 a 99999'
    : (f.numeroComprobante !== '' && !nroValido) ? 'El número va de 1 a 99999999'
      : (pideNumeroComprobante(f.puntoVenta, f.numeroComprobante) && (f.puntoVenta === '' || f.numeroComprobante === '')) ? 'Indicá el punto de venta y el número (los dos o ninguno)'
        : (f.cae !== '' && !/^\d{14}$/.test(f.cae)) ? 'El CAE tiene 14 dígitos'
          : null;
  return { numeroPrevio, error, pv, nro };
}

/** Datos del comprobante para el POST (letra, punto de venta y número, o el número como texto libre). */
function cuerpoIdentidad(f: Identidad, tipo: TipoNota) {
  const { numeroPrevio, pv, nro } = validarIdentidad(f, tipo);
  return {
    letra: f.letra || undefined,
    puntoVenta: numeroPrevio ? pv : undefined,
    numeroComprobante: numeroPrevio ? nro : undefined,
    numeroFactura: numeroPrevio ? undefined : (f.numeroFactura.trim() || undefined),
    cae: f.cae || undefined,
    caeVencimiento: f.caeVencimiento || undefined,
    fechaEmision: f.fechaEmision || undefined,
    notas: f.notas.trim() || undefined,
  };
}

function CamposIdentidad({ form, setForm, tipo }: { form: Identidad; setForm: (f: Identidad) => void; tipo: TipoNota }) {
  const { numeroPrevio, error } = validarIdentidad(form, tipo);
  const pideNumero = pideNumeroComprobante(form.puntoVenta, form.numeroComprobante);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">Letra</label>
          <select value={form.letra} onChange={e => setForm({ ...form, letra: e.target.value })} className="w-full px-3 py-2 border rounded-lg">
            <option value="">Sin letra</option>
            {LETRAS_COMPROBANTE.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Punto de venta{pideNumero && <Obligatorio />}</label>
          <input type="number" min={1} max={99999} aria-label="Punto de venta" required={pideNumero} value={form.puntoVenta} onChange={e => setForm({ ...form, puntoVenta: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Número{pideNumero && <Obligatorio />}</label>
          <input type="number" min={1} max={99999999} aria-label="Número del comprobante" required={pideNumero} value={form.numeroComprobante} onChange={e => setForm({ ...form, numeroComprobante: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Emisión</label>
          <input type="date" value={form.fechaEmision} onChange={e => setForm({ ...form, fechaEmision: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
        </div>
      </div>
      {numeroPrevio && <p className="text-sm text-slate-600">Comprobante: <span className="font-medium">{numeroPrevio}</span></p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}
      {!numeroPrevio && (
        <div>
          <label className="block text-sm font-medium mb-1">Número (texto libre, sin punto de venta y número)</label>
          <input value={form.numeroFactura} onChange={e => setForm({ ...form, numeroFactura: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium mb-1">CAE</label>
          <input inputMode="numeric" maxLength={14} placeholder="14 dígitos" value={form.cae} onChange={e => setForm({ ...form, cae: e.target.value.trim() })} className="w-full px-3 py-2 border rounded-lg" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Vencimiento del CAE</label>
          <input type="date" value={form.caeVencimiento} onChange={e => setForm({ ...form, caeVencimiento: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
        </div>
      </div>
    </div>
  );
}

// ─── Nota de crédito ───────────────────────────────────────

/** Renglón de la factura con lo que todavía se puede acreditar (GET facturas-venta/:id/nota-credito/disponible). */
interface RenglonDisponible {
  id: number; skuInterno: string | null; descripcion: string; cantidad: number; precioUnitario: number; subtotal: number;
  alicuota: number; cantidadDisponible: number; montoDisponible: number;
}

/** La nota de crédito es sólo plata: la mercadería devuelta entra con un remito de devolución (DECISIONES 2026-09-28). */
export const AVISO_DEVOLUCION_POR_REMITO = 'La mercadería devuelta se ingresa con un remito de devolución en Remitos.';
interface DisponibleNc { facturaId: number; esMercadoLibre: boolean; totalDisponible: number; pendienteCobro: number; items: RenglonDisponible[] }
interface LineaNc { elegido: boolean; cantidad: string; monto: string }

/** Importe sugerido: el resto del renglón si acredita todo lo que queda; si no, cantidad × precio sin pasar lo que queda. */
function montoSugerido(r: RenglonDisponible, cantidad: number): number {
  if (Math.abs(cantidad - r.cantidadDisponible) <= 1e-4) return r.montoDisponible;
  return Math.min(centavos(cantidad * r.precioUnitario), r.montoDisponible);
}

/** Primer error de los renglones elegidos (mismas reglas que el backend); null si están bien. */
function errorRenglonesNc(renglones: RenglonDisponible[], lineas: Record<number, LineaNc>): string | null {
  const elegidos = renglones.filter(r => lineas[r.id]?.elegido);
  if (elegidos.length === 0) return 'Elegí al menos un renglón de la factura';
  for (const r of elegidos) {
    const l = lineas[r.id];
    const cantidad = Number(l.cantidad || 0);
    const monto = Number(l.monto || 0);
    const n = `«${r.descripcion}»`;
    if (!(cantidad >= 0)) return `${n}: la cantidad no puede ser negativa`;
    if (cantidad > r.cantidadDisponible + 1e-4) return `${n}: quedan ${r.cantidadDisponible} unidades para acreditar`;
    if (!(monto > 0)) return `${n}: indicá el importe a acreditar (mayor a 0)`;
    if (monto > r.montoDisponible + 0.005) return `${n}: quedan ${pesos(r.montoDisponible)} para acreditar`;
  }
  return null;
}

export function NotaCreditoVentaModal({ factura, onClose, onCreada }: { factura: FacturaOrigenNota | null; onClose: () => void; onCreada: () => void }) {
  const toast = useToast();
  const { data: disponible, error: errorDisponible } = useApi<DisponibleNc>(factura ? `/api/v3/facturas-venta/${factura.id}/nota-credito/disponible` : null);
  const [form, setForm] = useState<Identidad>(identidadVacia());
  const [lineas, setLineas] = useState<Record<number, LineaNc>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setForm(identidadVacia(factura?.letra));
    setLineas({});
  }, [factura?.id]);
  // El formulario se limpia en un efecto al abrir: se compara contra el vacío, no contra la foto al abrir.
  const cierre = useCierreConfirmado(useHayCambios({ form, lineas }, !!factura, { form: identidadVacia(factura?.letra), lineas: {} }), onClose);
  // Obligatorios (backend: identidadComprobanteVenta y renglonesNotaCredito): cada renglón elegido, su importe (mayor a 0).
  // Que haya al menos un renglón elegido y los topes los avisa errorRenglonesNc.
  const obligatorios = useCamposObligatorios(!!factura);

  const renglones = disponible?.items ?? [];
  const cambiar = (r: RenglonDisponible, cambios: Partial<LineaNc>) => setLineas(prev => {
    const actual = prev[r.id] ?? { elegido: false, cantidad: '', monto: '' };
    const nueva = { ...actual, ...cambios };
    // Al cambiar la cantidad se sugiere el importe (se puede corregir: una bonificación acredita menos)
    if (cambios.cantidad !== undefined) nueva.monto = String(montoSugerido(r, cantidad4(Number(cambios.cantidad || 0))) || '');
    return { ...prev, [r.id]: nueva };
  });
  const elegir = (r: RenglonDisponible, elegido: boolean) => cambiar(r, elegido
    ? { elegido, cantidad: String(r.cantidadDisponible), monto: String(r.montoDisponible) }
    : { elegido });

  const elegidos = renglones.filter(r => lineas[r.id]?.elegido);
  const resumen = resumenIvaVenta(elegidos.map(r => ({ total: Number(lineas[r.id].monto || 0), alicuota: r.alicuota })));
  const total = resumen.total;
  const aplicado = disponible?.esMercadoLibre ? 0 : Math.min(total, disponible?.pendienteCobro ?? 0);
  const saldoAFavor = disponible?.esMercadoLibre ? 0 : centavos(total - aplicado);
  const errorLineas = disponible ? errorRenglonesNc(renglones, lineas) : null;
  const errorTotal = disponible && total > disponible.totalDisponible + 0.005
    ? `La nota supera lo que queda por acreditar de la factura (${pesos(disponible.totalDisponible)})` : null;

  const handleCreate = async () => {
    if (!factura || !disponible) return;
    if (!obligatorios.validar()) return;
    const err = validarIdentidad(form, 'nota_credito').error ?? errorLineas ?? errorTotal;
    if (err) { toast.addToast(err, 'error'); return; }
    setSubmitting(true);
    try {
      await api.post(`/api/v3/facturas-venta/${factura.id}/nota-credito`, {
        ...cuerpoIdentidad(form, 'nota_credito'),
        items: elegidos.map(r => ({
          facturaVentaItemId: r.id,
          cantidad: cantidad4(Number(lineas[r.id].cantidad || 0)),
          monto: centavos(Number(lineas[r.id].monto)),
        })),
      });
      toast.addToast('Nota de crédito registrada', 'success');
      onCreada();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={!!factura} cierre={cierre} title={`Nota de crédito sobre ${factura?.numeroFactura || `la factura #${factura?.id ?? ''}`}`} size="lg">
      <div {...obligatorios.contenedor} className="space-y-4 text-sm">
        <LeyendaObligatorio />
        {errorDisponible && <p className="text-red-600">{errorDisponible}</p>}
        {disponible?.esMercadoLibre && (
          <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sky-800">
            Factura de MercadoLibre: la cobró MercadoPago, que también hace los reintegros. La nota registra el comprobante y su IVA, pero no cambia lo que queda por cobrar ni genera saldo a favor.
          </div>
        )}
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-slate-600">
          La nota de crédito no mueve stock. {AVISO_DEVOLUCION_POR_REMITO}
        </p>
        <CamposIdentidad form={form} setForm={setForm} tipo="nota_credito" />
        <div>
          <p className="font-medium mb-1">Renglones de la factura <span className="font-normal text-slate-500">· importes con IVA incluido</span></p>
          {!disponible ? (
            <p className="text-slate-500">Cargando…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-1"></th><th>Renglón</th><th className="text-right">Alícuota</th>
                    <th className="text-right">Disponible</th><th className="text-right">Cantidad</th><th className="text-right">Importe<Obligatorio /></th>
                  </tr>
                </thead>
                <tbody>
                  {renglones.map(r => {
                    const l = lineas[r.id];
                    const agotado = r.montoDisponible <= 0.005;
                    return (
                      <tr key={r.id} className="border-t align-top">
                        <td className="py-1 pr-2"><input type="checkbox" disabled={agotado} checked={!!l?.elegido} onChange={e => elegir(r, e.target.checked)} /></td>
                        <td className="py-1">{r.descripcion}{r.skuInterno && <span className="block text-xs text-slate-500">{r.skuInterno}</span>}</td>
                        <td className="text-right py-1">{etiquetaAlicuota(r.alicuota)}</td>
                        <td className="text-right py-1 whitespace-nowrap">{`${r.cantidadDisponible} u. · ${pesos(r.montoDisponible)}`}</td>
                        <td className="text-right py-1">
                          <input type="number" min={0} step="any" disabled={!l?.elegido} value={l?.cantidad ?? ''} onChange={e => cambiar(r, { cantidad: e.target.value })} className="w-20 px-2 py-1 border rounded text-right disabled:bg-slate-100" />
                        </td>
                        <td className="text-right py-1">
                          <input type="number" min={0} step="0.01" disabled={!l?.elegido} aria-label={`Importe a acreditar de «${r.descripcion}»`} data-campo="Importe" {...faltaSi(!(Number(l?.monto || 0) > 0), !!l?.elegido)} value={l?.monto ?? ''} onChange={e => setLineas(prev => ({ ...prev, [r.id]: { ...prev[r.id], monto: e.target.value } }))} className="w-28 px-2 py-1 border rounded text-right disabled:bg-slate-100" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <p className="text-xs text-slate-500 mt-1">Podés acreditar menos que cantidad × precio (bonificación): corregí el importe. Con cantidad 0 se acredita solo el importe.</p>
        </div>
        {elegidos.length > 0 && (
          <div className="space-y-2">
            <p className="font-medium">IVA por alícuota</p>
            <TablaIvaPorAlicuota filas={resumen.porAlicuota} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
              <div><span className="text-slate-500">Total de la nota:</span> <span className="font-medium">{pesos(total)}</span></div>
              <div title="Baja lo que queda por cobrar de la factura"><span className="text-slate-500">Se aplica a la factura:</span> <span className="font-medium text-emerald-700">{pesos(aplicado)}</span></div>
              <div title="Lo que no entra en lo pendiente de la factura queda a favor del cliente"><span className="text-slate-500">Saldo a favor del cliente:</span> <span className="font-medium">{pesos(saldoAFavor)}</span></div>
            </div>
          </div>
        )}
        {(errorLineas && elegidos.length > 0) && <p className="text-red-600">{errorLineas}</p>}
        {errorTotal && <p className="text-red-600">{errorTotal}</p>}
        <div>
          <label className="block text-sm font-medium mb-1">Observaciones</label>
          <input value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={cierre.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
          <Button onClick={handleCreate} loading={submitting} disabled={!disponible}>
            {submitting ? 'Registrando...' : 'Registrar nota de crédito'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Nota de débito ────────────────────────────────────────

interface ItemNd { descripcion: string; cantidad: string; precioUnitario: string; alicuotaIva: string }
const itemNdVacio = (): ItemNd => ({ descripcion: '', cantidad: '1', precioUnitario: '', alicuotaIva: '21' });

export function NotaDebitoVentaModal({ factura, onClose, onCreada }: { factura: FacturaOrigenNota | null; onClose: () => void; onCreada: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState<Identidad>(identidadVacia());
  const [items, setItems] = useState<ItemNd[]>([itemNdVacio()]);
  const [fechaVencimiento, setFechaVencimiento] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setForm(identidadVacia(factura?.letra));
    setItems([itemNdVacio()]);
    setFechaVencimiento('');
  }, [factura?.id]);
  // El formulario se limpia en un efecto al abrir: se compara contra el vacío, no contra la foto al abrir.
  const cierre = useCierreConfirmado(
    useHayCambios({ form, items, fechaVencimiento }, !!factura, { form: identidadVacia(factura?.letra), items: [itemNdVacio()], fechaVencimiento: '' }),
    onClose,
  );
  // Obligatorios (backend: identidadComprobanteVenta y CreateNotaDebitoVentaDto): cada ítem, su cantidad (mayor a 0) y
  // su precio.
  const obligatorios = useCamposObligatorios(!!factura);

  const cambiar = (idx: number, campo: keyof ItemNd, valor: string) => setItems(prev => prev.map((it, i) => (i === idx ? { ...it, [campo]: valor } : it)));
  const subtotal = (it: ItemNd) => centavos(Number(it.cantidad || 0) * Number(it.precioUnitario || 0));
  const resumen = resumenIvaVenta(items.map(it => ({ total: subtotal(it), alicuota: Number(it.alicuotaIva) })));
  const errorItems = items.some(it => !it.descripcion.trim()) ? 'Cada ítem necesita una descripción'
    : items.some(it => !(Number(it.cantidad) > 0)) ? 'La cantidad de cada ítem tiene que ser mayor a 0'
      : items.some(it => !(Number(it.precioUnitario) >= 0) || it.precioUnitario === '') ? 'Indicá el precio de cada ítem'
        : !(resumen.total > 0) ? 'El total de la nota tiene que ser mayor a 0'
          : null;

  const handleCreate = async () => {
    if (!factura) return;
    if (!obligatorios.validar()) return;
    const err = validarIdentidad(form, 'nota_debito').error ?? errorItems;
    if (err) { toast.addToast(err, 'error'); return; }
    setSubmitting(true);
    try {
      await api.post(`/api/v3/facturas-venta/${factura.id}/nota-debito`, {
        ...cuerpoIdentidad(form, 'nota_debito'),
        fechaVencimiento: fechaVencimiento || undefined,
        items: items.map(it => ({
          descripcion: it.descripcion.trim(),
          cantidad: Number(it.cantidad),
          precioUnitario: Number(it.precioUnitario),
          alicuotaIva: Number(it.alicuotaIva),
        })),
      });
      toast.addToast('Nota de débito registrada', 'success');
      onCreada();
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={!!factura} cierre={cierre} title={`Nota de débito sobre ${factura?.numeroFactura || `la factura #${factura?.id ?? ''}`}`} size="lg">
      <div {...obligatorios.contenedor} className="space-y-4 text-sm">
        <LeyendaObligatorio />
        <CamposIdentidad form={form} setForm={setForm} tipo="nota_debito" />
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="font-medium">Ítems <span className="font-normal text-slate-500">· precios con IVA incluido</span></p>
            <button type="button" onClick={() => setItems([...items, itemNdVacio()])} className="text-blue-600 hover:text-blue-800 font-medium">+ Agregar ítem</button>
          </div>
          {items.map((it, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-12 sm:col-span-5">
                <label className="block text-xs text-slate-600 mb-1">Descripción</label>
                <input aria-label="Descripción" value={it.descripcion} onChange={e => cambiar(idx, 'descripcion', e.target.value)} className="w-full px-2 py-2 border rounded-lg" />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block text-xs text-slate-600 mb-1">Cantidad<Obligatorio /></label>
                <input type="number" min={0} step="any" aria-label="Cantidad" {...faltaSi(!(Number(it.cantidad) > 0))} value={it.cantidad} onChange={e => cambiar(idx, 'cantidad', e.target.value)} className="w-full px-2 py-2 border rounded-lg text-right" />
              </div>
              <div className="col-span-4 sm:col-span-2">
                <label className="block text-xs text-slate-600 mb-1">Precio<Obligatorio /></label>
                <input type="number" min={0} step="0.01" aria-label="Precio" {...faltaSi(it.precioUnitario === '')} value={it.precioUnitario} onChange={e => cambiar(idx, 'precioUnitario', e.target.value)} className="w-full px-2 py-2 border rounded-lg text-right" />
              </div>
              <div className="col-span-3 sm:col-span-2">
                <label className="block text-xs text-slate-600 mb-1">Alícuota</label>
                <select value={it.alicuotaIva} onChange={e => cambiar(idx, 'alicuotaIva', e.target.value)} className="w-full px-2 py-2 border rounded-lg">
                  {ALICUOTAS_IVA.map(a => <option key={a} value={a}>{etiquetaAlicuota(a)}</option>)}
                </select>
              </div>
              <div className="col-span-1 text-right">
                <button type="button" disabled={items.length === 1} onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-red-600 hover:text-red-800 px-1 py-2 disabled:opacity-40" title="Quitar ítem">Quitar</button>
              </div>
            </div>
          ))}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Vencimiento (opcional)</label>
          <input type="date" aria-label="Vencimiento" value={fechaVencimiento} onChange={e => setFechaVencimiento(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
          <p className="text-xs text-slate-500 mt-1">Si no lo indicás, vence según la condición de pago del cliente, contada desde la emisión de la nota.</p>
        </div>
        {resumen.porAlicuota.length > 0 && resumen.total > 0 && (
          <div>
            <p className="font-medium mb-1">IVA por alícuota</p>
            <TablaIvaPorAlicuota filas={resumen.porAlicuota} />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">Observaciones</label>
          <input value={form.notas} onChange={e => setForm({ ...form, notas: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={cierre.cerrar} className="px-4 py-2 text-sm">Cancelar</button>
          <Button onClick={handleCreate} loading={submitting}>
            {submitting ? 'Registrando...' : 'Registrar nota de débito'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
